import {BackendAPIBlueprint} from "../generated/blueprint.ts";
import {auth} from "../firebaseApp.ts";
import {getBackendApiBasePath} from "../lib/backendApiBaseUrl.ts";
import {getCurrentBusiness} from "../utils/OrgManagement.ts";

export function buildQueryString(queryParams: object): string {
    const presentParams: [string, string][] = Object.entries(queryParams)
        .filter(([, value]) => value !== null && value !== undefined)
        .map(([key, value]) => [key, String(value)]);

    return new URLSearchParams(presentParams).toString();
}

export class BackendApi extends BackendAPIBlueprint {
    private readonly basePath: string;
    public readonly token: string | undefined;
    private readonly defaultSignal: AbortSignal | undefined;

    constructor(token?: string, signal?: AbortSignal) {
        super();
        this.token = token;
        this.basePath = getBackendApiBasePath();
        this.defaultSignal = signal;
    }

    private async getFreshToken(): Promise<string> {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            throw new Error('No authenticated user available');
        }
        return await currentUser.getIdToken(false);
    }

    public withAbortSignal(signal: AbortSignal | undefined): BackendApi {
        return signal ? new BackendApi(this.token, signal) : this;
    }

    public async get(path: string, queryParams: object | null = null, signal?: AbortSignal) {
        const token = await this.getFreshToken();

        let uri = this.basePath + path

        if (queryParams) {
            uri = uri + "?" + buildQueryString(queryParams);
        }

        const headers: Record<string, string> = {Authorization: `Bearer ${token}`};

        const currentBusiness = getCurrentBusiness();
        if (currentBusiness != null) {
            headers['x-current-business-id'] = currentBusiness;
        }

        return await fetch(uri, {
            headers: headers,
            signal: signal ?? this.defaultSignal,
        });
    }

    public async post(path: string, body: object, signal?: AbortSignal): Promise<Response> {
        const token = await this.getFreshToken();

        const headers: Record<string, string> = {
            Authorization: `Bearer ${token}`,
            "content-type": "application/json"
        };

        const currentBusiness = getCurrentBusiness();
        if (currentBusiness) {
            headers['x-current-business-id'] = currentBusiness;
        }

        return await fetch(this.basePath + path, {
            method: "POST",
            body: JSON.stringify(body),
            headers: headers,
            signal: signal ?? this.defaultSignal,
        });
    }

    public async postFormData(path: string, body: FormData, signal?: AbortSignal, businessId?: string | null): Promise<Response> {
        const token = await this.getFreshToken();

        const headers: Record<string, string> = {
            Authorization: `Bearer ${token}`,
        };
        const currentBusiness = businessId === undefined ? getCurrentBusiness() : businessId;
        if (currentBusiness) {
            headers['x-current-business-id'] = currentBusiness;
        }

        return await fetch(this.basePath + path, {
            method: 'POST',
            body: body,
            headers: headers,
            signal: signal ?? this.defaultSignal,
        })
    }
}
